import { X, Trash2 } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useNavigate } from "react-router-dom";
import { formatINR } from "@/lib/brand";

export default function CartDrawer() {
  const { items, subtotal, remove, setQty, open, setOpen, clear } = useCart();
  const nav = useNavigate();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog">
      <div data-testid="cart-backdrop" className="absolute inset-0 bg-ink/60 animate-fade-in" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-ivory flex flex-col animate-fade-in text-ink">
        <div className="flex items-center justify-between px-8 py-6 border-b border-black/10">
          <div>
            <p className="text-[11px] tracking-widest-plus uppercase text-black/50">Your bag</p>
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/brand/looxs-wordmark.png`} alt="LOOXS" style={{ height: 44, width: "auto", display: "block", marginTop: 4 }} draggable="false" />
          </div>
          <button data-testid="cart-close-btn" onClick={() => setOpen(false)} aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.25} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-12 text-center">
              <p className="font-editorial text-3xl mb-3">The bag is empty.</p>
              <p className="text-sm text-black/60 leading-relaxed">Products are available in-store and via pre-order. Gift cards are available now.</p>
              <button onClick={() => { setOpen(false); nav("/gifts"); }} data-testid="bag-empty-gifts" className="mt-8 tracking-widest-plus text-[11px] uppercase link-draw">Explore gift cards →</button>
            </div>
          ) : (
            <ul className="divide-y divide-black/10">
              {items.map((it, i) => {
                const key = it.sku ?? it.slug ?? (it.kind === "gift_card" ? `gift-${it.gift_amount}` : `row-${i}`);
                return (
                <li key={key} data-testid={`bag-item-${i}`} className="p-6 flex gap-4">
                  <div className="w-20 h-24 border border-black/10 flex items-center justify-center font-editorial text-2xl text-gold">
                    ₹
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium leading-tight">{it.name}</p>
                    <p className="text-[10px] uppercase tracking-widest text-black/50 mt-1">Gift card · digital</p>
                    <div className="mt-3 flex items-center gap-3 text-sm">
                      <button className="w-7 h-7 border border-black/20 hover:border-black transition" onClick={() => setQty(i, it.quantity - 1)}>−</button>
                      <span className="w-4 text-center">{it.quantity}</span>
                      <button className="w-7 h-7 border border-black/20 hover:border-black transition" onClick={() => setQty(i, it.quantity + 1)}>+</button>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <span className="text-sm">{formatINR(it.price * it.quantity)}</span>
                    <button onClick={() => remove(i)} className="text-black/40 hover:text-gold" aria-label="Remove">
                      <Trash2 className="w-4 h-4" strokeWidth={1.25} />
                    </button>
                  </div>
                </li>
              );})}
            </ul>
          )}
        </div>
        <div className="border-t border-black/10 p-8 space-y-4">
          <div className="flex justify-between text-sm">
            <span className="tracking-widest-plus text-[11px] uppercase">Subtotal</span>
            <span data-testid="cart-subtotal">{formatINR(subtotal)}</span>
          </div>
          <button
            data-testid="cart-checkout-btn"
            disabled={items.length === 0}
            onClick={() => { setOpen(false); nav("/checkout"); }}
            className="w-full bg-ink text-ivory py-4 text-[11px] tracking-widest-plus uppercase hover:bg-gold hover:text-ink transition-colors duration-500 disabled:opacity-30"
          >
            Proceed to checkout
          </button>
          <p className="text-[10px] tracking-widest uppercase text-black/40 text-center">Only gift cards are sold online. Garments are available in-store or by pre-order.</p>
        </div>
      </aside>
    </div>
  );
}
